import React from 'react';

/* ── Spinner ──────────────────────────────────────────────────────────────── */
export function Spinner({ size = 20, color = 'var(--color-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ animation: 'spin 0.75s linear infinite', flexShrink: 0 }}
      aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2.5" strokeOpacity="0.2" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/* ── Button ───────────────────────────────────────────────────────────────── */
export function Btn({
  children, variant = 'primary', size = 'md',
  loading, fullWidth, style, icon, ...props
}) {
  const disabled = props.disabled || loading;

  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '7px', fontWeight: 600, letterSpacing: '-0.01em',
    borderRadius: 'var(--radius-md)',
    transition: `all var(--t-base) var(--ease-out)`,
    width: fullWidth ? '100%' : undefined,
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none', WebkitUserSelect: 'none',
    position: 'relative', overflow: 'hidden',
  };

  const sizes = {
    xs: { padding: '6px 12px',   fontSize: '12px', minHeight: '30px' },
    sm: { padding: '8px 16px',   fontSize: '13px', minHeight: '36px' },
    md: { padding: '11px 22px',  fontSize: '14px', minHeight: '44px' },
    lg: { padding: '14px 28px',  fontSize: '15px', minHeight: '50px' },
    xl: { padding: '16px 36px',  fontSize: '16px', minHeight: '56px' },
  };

  const variants = {
    primary: {
      background: 'var(--color-primary)',
      color: '#fff',
      boxShadow: 'var(--shadow-primary)',
    },
    secondary: {
      background: 'var(--color-surface)',
      color: 'var(--color-primary)',
      border: '1.5px solid var(--color-primary)',
      boxShadow: 'none',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--color-text-secondary)',
      border: '1.5px solid var(--color-border)',
    },
    soft: {
      background: 'var(--color-primary-lighter)',
      color: 'var(--color-primary)',
      border: 'none',
    },
    danger: {
      background: 'var(--color-danger)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(239,68,68,0.25)',
    },
    success: {
      background: 'var(--color-success)',
      color: '#fff',
    },
  };

  return (
    <button
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      {...props}
    >
      {loading
        ? <Spinner size={15} color={variant === 'primary' || variant === 'danger' || variant === 'success' ? '#fff' : 'var(--color-primary)'} />
        : <>
            {icon && <span style={{ fontSize: '16px', lineHeight: 1 }}>{icon}</span>}
            {children}
          </>
      }
    </button>
  );
}

/* ── Input ────────────────────────────────────────────────────────────────── */
export function Input({ label, error, hint, prefix, style, containerStyle, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', ...containerStyle }}>
      {label && (
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', letterSpacing: '-0.005em' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {prefix && (
          <span style={{
            position: 'absolute', left: '14px',
            fontSize: '14px', color: 'var(--color-text-muted)',
            pointerEvents: 'none', userSelect: 'none',
          }}>{prefix}</span>
        )}
        <input
          style={{
            width: '100%',
            padding: prefix ? '11px 14px 11px 38px' : '11px 14px',
            borderRadius: 'var(--radius-md)',
            border: `1.5px solid ${error ? 'var(--color-danger)' : 'var(--color-border)'}`,
            background: 'var(--color-surface)',
            outline: 'none',
            minHeight: '46px',
            fontSize: '15px',
            color: 'var(--color-text)',
            transition: `border-color var(--t-fast) var(--ease-out), box-shadow var(--t-fast) var(--ease-out)`,
            ...style,
          }}
          onFocus={e => {
            e.target.style.borderColor = 'var(--color-primary)';
            e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
          }}
          onBlur={e => {
            e.target.style.borderColor = error ? 'var(--color-danger)' : 'var(--color-border)';
            e.target.style.boxShadow = 'none';
          }}
          {...props}
        />
      </div>
      {error && <span style={{ fontSize: '12px', color: 'var(--color-danger)', fontWeight: 500 }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{hint}</span>}
    </div>
  );
}

/* ── Select ───────────────────────────────────────────────────────────────── */
export function Select({ label, error, style, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>{label}</label>}
      <select
        style={{
          width: '100%', padding: '11px 38px 11px 14px',
          borderRadius: 'var(--radius-md)',
          border: `1.5px solid ${error ? 'var(--color-danger)' : 'var(--color-border)'}`,
          background: `var(--color-surface) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 14px center`,
          backgroundSize: '12px',
          outline: 'none', minHeight: '46px',
          fontSize: '15px', color: 'var(--color-text)',
          cursor: 'pointer',
          ...style,
        }}
        {...props}
      >{children}</select>
      {error && <span style={{ fontSize: '12px', color: 'var(--color-danger)', fontWeight: 500 }}>{error}</span>}
    </div>
  );
}

/* ── Textarea ─────────────────────────────────────────────────────────────── */
export function Textarea({ label, error, style, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>{label}</label>}
      <textarea
        style={{
          width: '100%', padding: '12px 14px',
          borderRadius: 'var(--radius-md)',
          border: `1.5px solid ${error ? 'var(--color-danger)' : 'var(--color-border)'}`,
          background: 'var(--color-surface)',
          outline: 'none', fontSize: '15px',
          color: 'var(--color-text)',
          resize: 'vertical', minHeight: '100px',
          transition: `border-color var(--t-fast), box-shadow var(--t-fast)`,
          ...style,
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
        onBlur={e  => { e.target.style.borderColor = error ? 'var(--color-danger)' : 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
        {...props}
      />
      {error && <span style={{ fontSize: '12px', color: 'var(--color-danger)', fontWeight: 500 }}>{error}</span>}
    </div>
  );
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
export function Card({ children, style, interactive, ...props }) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-card)',
        cursor: interactive ? 'pointer' : undefined,
        transition: interactive ? `box-shadow var(--t-base), transform var(--t-base)` : undefined,
        ...style,
      }}
      onMouseEnter={interactive ? e => { e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.transform = 'translateY(-1px)'; } : undefined}
      onMouseLeave={interactive ? e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'none'; } : undefined}
      {...props}
    >{children}</div>
  );
}

/* ── Avatar ───────────────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  ['#EFF6FF','#1D4ED8'], ['#F0FDF4','#15803D'], ['#FFF7ED','#C2410C'],
  ['#FDF4FF','#7E22CE'], ['#FFFBEB','#B45309'], ['#FFF1F2','#BE123C'],
];
export function Avatar({ name = '?', size = 36, style }) {
  const i   = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  const [bg, fg] = AVATAR_COLORS[i];
  const initials = name.split(' ').filter(Boolean).slice(0,2).map(w => w[0]).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700,
      flexShrink: 0, letterSpacing: '-0.02em', userSelect: 'none',
      ...style,
    }}>{initials || '?'}</div>
  );
}

/* ── Badge ────────────────────────────────────────────────────────────────── */
const PRIORITY_META = {
  LOW:      { bg: '#F1F5F9', color: '#475569', label: 'Low'      },
  MEDIUM:   { bg: '#EFF6FF', color: '#1D4ED8', label: 'Medium'   },
  HIGH:     { bg: '#FFF7ED', color: '#C2410C', label: 'High'     },
  CRITICAL: { bg: '#FEF2F2', color: '#DC2626', label: 'Critical' },
};
const STATUS_META = {
  PENDING:     { bg: '#F1F5F9', color: '#475569', label: 'Pending'     },
  IN_PROGRESS: { bg: '#EFF6FF', color: '#1D4ED8', label: 'In Progress' },
  ON_HOLD:     { bg: '#FFFBEB', color: '#B45309', label: 'On Hold'     },
  DONE:        { bg: '#F0FDF4', color: '#15803D', label: 'Done'        },
  OVERDUE:     { bg: '#FEF2F2', color: '#DC2626', label: 'Overdue'     },
};

function Pill({ label, bg, color, dot }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 10px', borderRadius: 'var(--radius-full)',
      fontSize: '12px', fontWeight: 600,
      background: bg, color,
      whiteSpace: 'nowrap', letterSpacing: '0.01em',
    }}>
      {dot && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: color, flexShrink: 0 }} />}
      {label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.MEDIUM;
  return <Pill label={m.label} bg={m.bg} color={m.color} />;
}
export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  return <Pill label={m.label} bg={m.bg} color={m.color} dot />;
}
export function RoleBadge({ role }) {
  const map = {
    SUPER_ADMIN: { label: 'Super Admin', bg: '#FAF5FF', color: '#6D28D9' },
    ADMIN:       { label: 'Admin',       bg: '#EFF6FF', color: '#1D4ED8' },
    EMPLOYEE:    { label: 'Employee',    bg: '#F0FDF4', color: '#15803D' },
  };
  const m = map[role] || map.EMPLOYEE;
  return <Pill label={m.label} bg={m.bg} color={m.color} />;
}

/* ── Alert ────────────────────────────────────────────────────────────────── */
const ALERT_META = {
  error:   { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', icon: '⚠' },
  success: { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', icon: '✓' },
  warning: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A', icon: '⚡' },
  info:    { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', icon: 'ℹ' },
};
export function Alert({ type = 'error', message }) {
  if (!message) return null;
  const s = ALERT_META[type];
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '12px 16px', borderRadius: 'var(--radius-md)',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      fontSize: '14px', fontWeight: 500, lineHeight: 1.5,
    }}>
      <span style={{ flexShrink: 0, fontSize: '16px', lineHeight: 1 }}>{s.icon}</span>
      <span>{message}</span>
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────────────────── */
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      textAlign: 'center', padding: '56px 24px',
    }}>
      {icon && (
        <div style={{
          width: '64px', height: '64px', borderRadius: 'var(--radius-xl)',
          background: 'var(--color-bg-alt)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '28px', marginBottom: '16px',
        }}>{icon}</div>
      )}
      <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '6px' }}>{title}</p>
      {subtitle && <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', maxWidth: '280px' }}>{subtitle}</p>}
      {action && <div style={{ marginTop: '20px' }}>{action}</div>}
    </div>
  );
}

/* ── Page header ──────────────────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, action, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: '16px', marginBottom: '24px', flexWrap: 'wrap',
    }}>
      <div>
        <h1 style={{
          fontSize: '22px', fontWeight: 700, color: 'var(--color-text)',
          letterSpacing: '-0.03em', lineHeight: 1.2,
        }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '4px' }}>{subtitle}</p>}
        {children}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

/* ── Stat card ────────────────────────────────────────────────────────────── */
export function StatCard({ label, value, sub, color = 'var(--color-primary)', icon, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-card)',
        padding: '18px 20px',
        cursor: onClick ? 'pointer' : 'default',
        transition: `box-shadow var(--t-base), transform var(--t-base)`,
        position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={onClick ? e => { e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.transform = 'translateY(-2px)'; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'none'; } : undefined}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
        background: color, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
        <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-muted)', letterSpacing: '0.01em' }}>{label}</p>
        {icon && <span style={{ fontSize: '18px', opacity: 0.6 }}>{icon}</span>}
      </div>
      <p style={{ fontSize: '30px', fontWeight: 700, color, letterSpacing: '-0.03em', lineHeight: 1 }}>{value ?? 0}</p>
      {sub && <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px' }}>{sub}</p>}
    </div>
  );
}

/* ── Section heading ──────────────────────────────────────────────────────── */
export function SectionHeading({ children, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
      <h2 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {children}
      </h2>
      {action}
    </div>
  );
}

/* ── Divider ──────────────────────────────────────────────────────────────── */
export function Divider({ style }) {
  return <div style={{ height: '1px', background: 'var(--color-border)', margin: '16px 0', ...style }} />;
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
export function Skeleton({ width = '100%', height = 16, style }) {
  return (
    <div className="skeleton" style={{ width, height, borderRadius: 'var(--radius-sm)', ...style }} />
  );
}

/* ── Toast ────────────────────────────────────────────────────────────────── */
export function SuccessBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '12px 16px', borderRadius: 'var(--radius-md)',
      background: '#F0FDF4', border: '1px solid #BBF7D0',
      color: '#15803D', fontSize: '14px', fontWeight: 500,
      marginBottom: '16px',
    }}>
      <span style={{ fontSize: '16px' }}>✓</span>
      <span style={{ flex: 1 }}>{message}</span>
      {onDismiss && <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#15803D', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>}
    </div>
  );
}

/* ── Modal backdrop ───────────────────────────────────────────────────────── */
export function Modal({ children, onClose, title, maxWidth = '520px' }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-modal)',
        background: 'var(--color-overlay)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl) var(--radius-xl) var(--radius-lg) var(--radius-lg)',
        width: '100%', maxWidth,
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-xl)',
        animation: 'slideUp 0.2s var(--ease-out)',
      }}>
        {(title || onClose) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 24px 0',
          }}>
            {title && <h3 style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.02em' }}>{title}</h3>}
            {onClose && (
              <button onClick={onClose} style={{
                background: 'var(--color-bg-alt)', border: 'none',
                width: '30px', height: '30px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', color: 'var(--color-text-muted)', cursor: 'pointer',
                lineHeight: 1,
              }}>×</button>
            )}
          </div>
        )}
        <div style={{ padding: '20px 24px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Search input ─────────────────────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder = 'Search…', style }) {
  return (
    <div style={{ position: 'relative', ...style }}>
      <span style={{
        position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
        color: 'var(--color-text-muted)', fontSize: '16px', pointerEvents: 'none',
      }}>🔍</span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '11px 14px 11px 42px',
          borderRadius: 'var(--radius-full)',
          border: '1.5px solid var(--color-border)',
          background: 'var(--color-surface)',
          fontSize: '14px', color: 'var(--color-text)',
          outline: 'none',
          transition: `border-color var(--t-fast), box-shadow var(--t-fast)`,
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
        onBlur={e  => { e.target.style.borderColor = 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );
}

/* ── Chip filter ──────────────────────────────────────────────────────────── */
export function ChipFilter({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '7px 16px', borderRadius: 'var(--radius-full)',
              border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: active ? 'var(--color-primary)' : 'var(--color-surface)',
              color: active ? '#fff' : 'var(--color-text-secondary)',
              fontSize: '13px', fontWeight: active ? 600 : 400,
              cursor: 'pointer', transition: 'all var(--t-fast)',
              whiteSpace: 'nowrap',
            }}
          >{opt.label}</button>
        );
      })}
    </div>
  );
}
